import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { format, startOfMonth, endOfMonth, subMonths, isValid, parseISO } from 'date-fns';
import { de } from 'date-fns/locale';
import { Plus, FileText, X, Receipt, ChevronLeft, ChevronRight, Trash2, PlusCircle, History, Lock, Euro, Download, ExternalLink, Search, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Card,
  CardContent,
} from '../components/ui/card';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';
import { Badge } from '../components/ui/badge';
import { GoBDDeleteConfirmDialog, useGoBDDelete } from '../components/GoBDDeleteConfirmDialog';
import { AuditHistoryPanel } from '../components/AuditHistoryPanel';
import SearchableSelect from '../components/SearchableSelect';
import PdfCanvasPreview from '../components/PdfCanvasPreview';
import {
  DEFAULT_PERMISSIONS, mayDo, formatCurrency, formatDate, getReferenceLabel,
  getStatusBadge, getProvenanceInfo, handleApiError,
} from '../components/billing/billingUtils';
import BillingSuggestions from '../components/billing/BillingSuggestions';

export default function BillingPage() {
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isGenerateOpen, setIsGenerateOpen] = useState(false);
  // L6b: Reiter – Standard „Abzurechnen“ für Rollen mit Recht „Rechnung erstellen“
  const [tab, setTab] = useState(null);
  
  // Billing permissions
  const [permissions, setPermissions] = useState(DEFAULT_PERMISSIONS);
  
  // Billing adjustment permission
  const [accountingMayAdjust, setAccountingMayAdjust] = useState(false);
  
  // Filter state
  const [filters, setFilters] = useState({
    status: 'open', // 'open' = draft, approved, issued
    customer_id: '',
    date_from: '',
    date_to: '',
    search: ''
  });
  
  // Billing quantity adjustment dialog
  const [adjustDialogOpen, setAdjustDialogOpen] = useState(false);
  const [adjustingEntry, setAdjustingEntry] = useState(null);
  const [adjustForm, setAdjustForm] = useState({
    billed_hours: '',
    reason: '',
    reason_other: '',
  });
  const [isAdjusting, setIsAdjusting] = useState(false);
  
  // Adjustment reason options
  const adjustmentReasons = [
    'Vor- und Nachbereitung',
    'Recherche und Einarbeitung',
    'Nicht erfasste Bearbeitungszeit',
    'Budgetgrenze erreicht',
    'Kulanz / Nachlass',
    'Mindesteinsatzzeit nach Vertrag',
    'Ausfallvergütung § 13a',
    'Rundung',
    'Sonstiges',
  ];
  
  // Helper: effective hours for billing
  const effHours = (e) => (e.billed_hours !== null && e.billed_hours !== undefined) ? e.billed_hours : e.hours;
  
  // Permission check helper
  const may = useCallback((action) => mayDo(user, permissions, action), [user, permissions]);
  const activeTab = tab || (may('create') ? 'open' : 'invoices');
  
  // Permission check for billing adjustment
  const canAdjust = user?.role === 'admin' || user?.role === 'supervisor' || (user?.role === 'accounting' && accountingMayAdjust);
  
  // GoBD Delete Dialog hook
  const { 
    dialogOpen: deleteDialogOpen, 
    setDialogOpen: setDeleteDialogOpen, 
    deleteConfig, 
    requestDelete, 
    handleConfirm: handleDeleteConfirm, 
    handleCancel: handleDeleteCancel 
  } = useGoBDDelete();
  
  // Audit History state
  const [historyPanelOpen, setHistoryPanelOpen] = useState(false);
  const [historyEntry, setHistoryEntry] = useState({ collection: '', id: '' });
  
  const showHistory = (collection, id) => {
    setHistoryEntry({ collection, id });
    setHistoryPanelOpen(true);
  };
  
  // Two-step invoice generation
  const [generateStep, setGenerateStep] = useState(1);
  const [previewEntries, setPreviewEntries] = useState({ project_entries: [], mileage_entries: [] });
  const [selectedProjectEntries, setSelectedProjectEntries] = useState([]);
  const [selectedMileageEntries, setSelectedMileageEntries] = useState([]);
  const [loadingPreview, setLoadingPreview] = useState(false);
  
  // Manual items for invoice
  const [manualItems, setManualItems] = useState([]);
  const [newManualItem, setNewManualItem] = useState({
    description: '',
    quantity: 1,
    unit: 'Stunden',
    unit_price: 0
  });
  
  // Generate invoice confirmation dialog
  const [confirmGenerateOpen, setConfirmGenerateOpen] = useState(false);
  const [confirmGenerateData, setConfirmGenerateData] = useState({
    periodStart: '',
    periodEnd: '',
    referenceValue: '',
    invoiceNote: '',
    customerInvoiceOptions: null,
    suggestedPeriod: null,
  });
  const [isGeneratingInvoice, setIsGeneratingInvoice] = useState(false);
  
  // PDF Preview dialog
  const [pdfDialogOpen, setPdfDialogOpen] = useState(false);
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
  const [pdfBlob, setPdfBlob] = useState(null);
  const [pdfFilename, setPdfFilename] = useState('Rechnung.pdf');
  const [pdfLoading, setPdfLoading] = useState(false);
  
  const lastMonth = subMonths(new Date(), 1);
  const [generateForm, setGenerateForm] = useState({
    customer_id: '',
    project_id: '',
    period_start: format(startOfMonth(lastMonth), 'yyyy-MM-dd'),
    period_end: format(endOfMonth(lastMonth), 'yyyy-MM-dd'),
    billing_cycle: 'monthly',
  });

  const fetchData = useCallback(async () => {
    if (!api) return;
    try {
      setLoading(true);
      const [invoicesRes, customersRes, projectsRes, billingAdjustRes, permissionsRes] = await Promise.all([
        api.get('/billing/invoices'),
        api.get('/customers'),
        api.get('/projects'),
        api.get('/company-settings/billing-adjustment').catch(() => ({ data: { accounting_may_adjust_billing_quantities: false } })),
        api.get('/company-settings/billing-permissions').catch(() => ({ data: DEFAULT_PERMISSIONS })),
      ]);
      setInvoices(invoicesRes.data);
      setCustomers(customersRes.data);
      setProjects(projectsRes.data);
      setAccountingMayAdjust(billingAdjustRes.data?.accounting_may_adjust_billing_quantities ?? false);
      setPermissions(permissionsRes.data || DEFAULT_PERMISSIONS);
    } catch (error) {
      toast.error('Fehler beim Laden');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Filter invoices
  const filteredInvoices = useMemo(() => {
    return invoices.filter(invoice => {
      // Status filter
      if (filters.status === 'open') {
        if (!['draft', 'approved', 'issued'].includes(invoice.status)) return false;
      } else if (filters.status && filters.status !== 'all') {
        if (invoice.status !== filters.status) return false;
      }
      
      // Customer filter
      if (filters.customer_id && invoice.customer_id !== filters.customer_id) return false;
      
      // Date filter (on invoice_date or created_at for drafts)
      const dateToCheck = invoice.invoice_date || invoice.created_at?.split('T')[0];
      if (filters.date_from && dateToCheck && dateToCheck < filters.date_from) return false;
      if (filters.date_to && dateToCheck && dateToCheck > filters.date_to) return false;
      
      // Search filter
      if (filters.search) {
        const searchLower = filters.search.toLowerCase();
        const matchNumber = (invoice.invoice_number || '').toLowerCase().includes(searchLower);
        const matchDraft = (invoice.draft_reference || '').toLowerCase().includes(searchLower);
        const matchCustomer = (invoice.customer_name || '').toLowerCase().includes(searchLower);
        if (!matchNumber && !matchDraft && !matchCustomer) return false;
      }
      
      return true;
    });
  }, [invoices, filters]);
  
  // Calculate filter totals
  const filterTotals = useMemo(() => {
    return filteredInvoices.reduce((acc, inv) => ({
      count: acc.count + 1,
      net: acc.net + (inv.net_total || 0),
      gross: acc.gross + (inv.gross_total || 0)
    }), { count: 0, net: 0, gross: 0 });
  }, [filteredInvoices]);
  
  // Check if any filter is active
  const hasActiveFilter = filters.status !== 'open' || filters.customer_id || filters.date_from || filters.date_to || filters.search;
  
  // Reset filters
  const resetFilters = () => {
    setFilters({
      status: 'open',
      customer_id: '',
      date_from: '',
      date_to: '',
      search: ''
    });
  };

  // Reset dialog state when opening
  const openGenerateDialog = () => {
    setGenerateStep(1);
    setPreviewEntries({ project_entries: [], mileage_entries: [] });
    setSelectedProjectEntries([]);
    setSelectedMileageEntries([]);
    setManualItems([]);
    setNewManualItem({ description: '', quantity: 1, unit: 'Stunden', unit_price: 0 });
    setIsGenerateOpen(true);
  };

  // Step 1: Load preview entries
  const handleLoadPreview = async () => {
    if (!generateForm.customer_id) {
      toast.error('Bitte wählen Sie einen Kunden');
      return;
    }

    try {
      setLoadingPreview(true);
      const params = new URLSearchParams();
      params.append('customer_id', generateForm.customer_id);
      if (generateForm.project_id && generateForm.project_id !== 'all') {
        params.append('project_id', generateForm.project_id);
      }
      if (generateForm.period_start) {
        params.append('start_date', generateForm.period_start);
      }
      if (generateForm.period_end) {
        params.append('end_date', generateForm.period_end);
      }
      
      const response = await api.get(`/billing/preview?${params.toString()}`);
      
      if (response.data.entry_count === 0) {
        toast.warning('Keine offenen Einträge für diesen Zeitraum gefunden');
        return;
      }
      
      setPreviewEntries(response.data);
      setSelectedProjectEntries(response.data.project_entries.map(e => e.id));
      setSelectedMileageEntries(response.data.mileage_entries.map(e => e.id));
      setGenerateStep(2);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Laden der Einträge');
    } finally {
      setLoadingPreview(false);
    }
  };

  // Manual items management
  const addManualItem = () => {
    if (!newManualItem.description.trim()) {
      toast.error('Bitte geben Sie eine Beschreibung ein');
      return;
    }
    if (newManualItem.quantity <= 0) {
      toast.error('Die Menge muss größer als 0 sein');
      return;
    }
    
    setManualItems([...manualItems, { ...newManualItem, id: Date.now().toString() }]);
    setNewManualItem({ description: '', quantity: 1, unit: 'Stunden', unit_price: 0 });
  };

  const removeManualItem = (itemId) => {
    setManualItems(manualItems.filter(item => item.id !== itemId));
  };

  // Toggle entry selection
  const toggleProjectEntry = (entryId) => {
    setSelectedProjectEntries(prev => 
      prev.includes(entryId) 
        ? prev.filter(id => id !== entryId)
        : [...prev, entryId]
    );
  };

  const toggleMileageEntry = (entryId) => {
    setSelectedMileageEntries(prev => 
      prev.includes(entryId) 
        ? prev.filter(id => id !== entryId)
        : [...prev, entryId]
    );
  };

  // Select/deselect all
  const selectAllProjectEntries = () => {
    setSelectedProjectEntries(previewEntries.project_entries.map(e => e.id));
  };

  const deselectAllProjectEntries = () => {
    setSelectedProjectEntries([]);
  };

  // Calculate selected totals
  const getSelectedTotals = () => {
    const selectedProjects = previewEntries.project_entries.filter(e => selectedProjectEntries.includes(e.id));
    const selectedMileage = previewEntries.mileage_entries.filter(e => selectedMileageEntries.includes(e.id));
    
    const recordedHours = selectedProjects.filter(e => !e.is_daily_rate).reduce((sum, e) => sum + e.hours, 0);
    const trackedHours = selectedProjects.filter(e => !e.is_daily_rate).reduce((sum, e) => sum + effHours(e), 0);
    const manualHours = manualItems.filter(item => item.unit === 'Stunden').reduce((sum, item) => sum + item.quantity, 0);
    const hours = trackedHours + manualHours;
    const dailyRates = selectedProjects.filter(e => e.is_daily_rate).length;
    const km = selectedMileage.reduce((sum, e) => sum + e.kilometers, 0);
    
    return { 
      hours: hours.toFixed(2),
      recordedHours: recordedHours.toFixed(2),
      dailyRates, 
      km: km.toFixed(1), 
      count: selectedProjects.length + selectedMileage.length,
      manualCount: manualItems.length
    };
  };

  // Open generate confirmation dialog
  const openConfirmGenerateDialog = async () => {
    if (selectedProjectEntries.length === 0 && selectedMileageEntries.length === 0 && manualItems.length === 0) {
      toast.error('Bitte wählen Sie mindestens einen Eintrag aus oder fügen Sie manuelle Positionen hinzu');
      return;
    }
    
    try {
      const customerOptionsRes = await api.get(`/customers/${generateForm.customer_id}/invoice-options`).catch(() => ({ data: {} }));
      
      const suggestedPeriod = previewEntries.suggested_period || {
        start: generateForm.period_start,
        end: generateForm.period_end,
        interval: 'none'
      };
      
      setConfirmGenerateData({
        periodStart: suggestedPeriod.start,
        periodEnd: suggestedPeriod.end,
        referenceValue: customerOptionsRes.data?.reference_value || '',
        invoiceNote: '',
        customerInvoiceOptions: customerOptionsRes.data,
        suggestedPeriod: suggestedPeriod,
      });
      setConfirmGenerateOpen(true);
    } catch (error) {
      toast.error('Fehler beim Laden der Kundenoptionen');
    }
  };
  
  // Generate invoice with confirmation data
  const handleConfirmGenerate = async () => {
    setIsGeneratingInvoice(true);
    try {
      const payload = {
        customer_id: generateForm.customer_id,
        project_entry_ids: selectedProjectEntries,
        mileage_entry_ids: selectedMileageEntries,
        manual_items: manualItems,
      };
      
      if (confirmGenerateData.suggestedPeriod) {
        if (confirmGenerateData.periodStart !== confirmGenerateData.suggestedPeriod.start ||
            confirmGenerateData.periodEnd !== confirmGenerateData.suggestedPeriod.end) {
          payload.period_start = confirmGenerateData.periodStart;
          payload.period_end = confirmGenerateData.periodEnd;
        }
      }
      
      if (confirmGenerateData.referenceValue) {
        payload.reference_value = confirmGenerateData.referenceValue;
      }
      
      if (confirmGenerateData.invoiceNote) {
        payload.invoice_note = confirmGenerateData.invoiceNote;
      }
      
      const response = await api.post('/billing/invoices/generate-selected', payload);
      const invoice = response.data.invoice;
      toast.success(`Rechnungsentwurf erstellt: ${invoice.draft_reference}`);
      
      setConfirmGenerateOpen(false);
      setIsGenerateOpen(false);
      setGenerateStep(1);
      fetchData();
      // Entwurf gleich öffnen – Positionen prüfen und bearbeiten
      openInvoiceDetail(invoice.id);
    } catch (error) {
      handleApiError(error);
    } finally {
      setIsGeneratingInvoice(false);
    }
  };
  
  // Format billing interval for display
  const formatBillingInterval = (interval) => {
    const labels = { monthly: 'monatlich', quarterly: 'quartalsweise', none: 'nach Aufwand' };
    return labels[interval] || interval;
  };
  
  // PDF Preview functions
  const openPdfPreview = async (invoiceId) => {
    setPdfLoading(true);
    setPdfDialogOpen(true);
    
    try {
      const response = await api.get(`/billing/invoices/${invoiceId}/pdf`, { responseType: 'blob' });
      
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'Rechnung.pdf';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, '');
        }
      }
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const blobUrl = URL.createObjectURL(blob);
      setPdfBlob(blob);
      setPdfBlobUrl(blobUrl);
      setPdfFilename(filename);
    } catch (error) {
      toast.error('Das PDF konnte nicht erzeugt werden.');
      setPdfDialogOpen(false);
    } finally {
      setPdfLoading(false);
    }
  };
  
  const closePdfPreview = () => {
    if (pdfBlobUrl) {
      URL.revokeObjectURL(pdfBlobUrl);
      setPdfBlobUrl(null);
    }
    setPdfBlob(null);
    setPdfDialogOpen(false);
  };
  
  const downloadPdf = () => {
    if (pdfBlobUrl) {
      const link = document.createElement('a');
      link.href = pdfBlobUrl;
      link.download = pdfFilename;
      link.click();
    }
  };
  
  // L6a: Rechnung auf eigener Seite öffnen statt im Dialog
  const openInvoiceDetail = (invoiceId) => {
    navigate(`/billing/invoices/${invoiceId}`);
  };

  // Billing quantity adjustment functions
  const openAdjustDialog = (entry) => {
    setAdjustingEntry(entry);
    const currentBilledHours = entry.billed_hours !== null && entry.billed_hours !== undefined 
      ? entry.billed_hours 
      : entry.hours;
    setAdjustForm({
      billed_hours: currentBilledHours.toString(),
      reason: '',
      reason_other: '',
    });
    setAdjustDialogOpen(true);
  };

  const handleAdjustSubmit = async () => {
    if (!adjustingEntry) return;
    
    const billedHours = parseFloat(adjustForm.billed_hours);
    
    if (isNaN(billedHours) || billedHours < 0) {
      toast.error('Bitte geben Sie eine gültige Stundenzahl ein (>= 0)');
      return;
    }
    
    const remainder = Math.round((billedHours % 0.25) * 100) / 100;
    if (remainder !== 0) {
      toast.error('Stunden müssen in 15-Minuten-Intervallen (0,25h) angegeben werden');
      return;
    }
    
    if (!adjustForm.reason) {
      toast.error('Bitte wählen Sie einen Grund aus');
      return;
    }
    
    let finalReason = adjustForm.reason;
    if (adjustForm.reason === 'Sonstiges') {
      if (!adjustForm.reason_other.trim()) {
        toast.error('Bitte geben Sie bei "Sonstiges" einen Grund ein');
        return;
      }
      finalReason = `Sonstiges: ${adjustForm.reason_other.trim()}`;
    }
    
    setIsAdjusting(true);
    try {
      const savedProjectSelection = [...selectedProjectEntries];
      const savedMileageSelection = [...selectedMileageEntries];
      
      await api.put(`/project-time/${adjustingEntry.id}/billing-quantity`, {
        billed_hours: billedHours,
        adjustment_reason: finalReason,
      });
      
      toast.success('Abrechnungsmenge angepasst');
      setAdjustDialogOpen(false);
      
      await handleLoadPreview();
      setSelectedProjectEntries(savedProjectSelection);
      setSelectedMileageEntries(savedMileageSelection);
    } catch (error) {
      handleApiError(error);
    } finally {
      setIsAdjusting(false);
    }
  };

  const handleRemoveAdjustment = async () => {
    if (!adjustingEntry) return;
    
    setIsAdjusting(true);
    try {
      const savedProjectSelection = [...selectedProjectEntries];
      const savedMileageSelection = [...selectedMileageEntries];
      
      await api.put(`/project-time/${adjustingEntry.id}/billing-quantity`, {
        billed_hours: null,
        adjustment_reason: null,
      });
      
      toast.success('Anpassung entfernt');
      setAdjustDialogOpen(false);
      
      await handleLoadPreview();
      setSelectedProjectEntries(savedProjectSelection);
      setSelectedMileageEntries(savedMileageSelection);
    } catch (error) {
      handleApiError(error);
    } finally {
      setIsAdjusting(false);
    }
  };

  const filteredProjects = projects.filter(
    p => !generateForm.customer_id || p.customer_id === generateForm.customer_id
  );

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    try {
      const date = typeof dateString === 'string' ? parseISO(dateString) : new Date(dateString);
      return isValid(date) ? format(date, 'dd.MM.yyyy HH:mm', { locale: de }) : '-';
    } catch {
      return '-';
    }
  };

  return (
    <div className="p-4 md:p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <Receipt className="w-6 h-6" />
            Abrechnung
          </h1>
          <p className="text-muted-foreground mt-1">Rechnungen erstellen und verwalten</p>
        </div>
        {may('create') && (
          <Button type="button" variant="outline" onClick={openGenerateDialog} className="border-border" title="Einträge selbst auswählen oder Rechnung nur mit manuellen Positionen">
            <Plus className="w-4 h-4 mr-2" />
            Manuell erstellen
          </Button>
        )}
      </div>

      {/* L6b: Reiter Abzurechnen | Rechnungen */}
      <div className="flex gap-1 border-b border-border" role="tablist">
        {[['open', 'Abzurechnen'], ['invoices', 'Rechnungen']].map(([key, label]) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={activeTab === key}
            onClick={() => setTab(key)}
            className={`px-4 py-2 text-sm -mb-px border-b-2 ${activeTab === key ? 'border-cyan-500 text-foreground font-medium' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
          >
            {label}
          </button>
        ))}
      </div>

      {activeTab === 'open' && (
        <BillingSuggestions api={api} canCreate={may('create')} onOpenInvoice={openInvoiceDetail} />
      )}

      {activeTab === 'invoices' && (<>
      {/* Filters */}
      <Card className="bg-card/50 border-border">
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            {/* Status Filter */}
            <div className="space-y-1">
              <Label className="text-muted-foreground text-xs">Status</Label>
              <Select value={filters.status} onValueChange={(value) => setFilters({...filters, status: value})}>
                <SelectTrigger className="bg-input border-border text-foreground">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-card border-border">
                  <SelectItem value="open">Alle offenen</SelectItem>
                  <SelectItem value="all">Alle</SelectItem>
                  <SelectItem value="draft">Entwurf</SelectItem>
                  <SelectItem value="approved">Freigegeben</SelectItem>
                  <SelectItem value="issued">Ausgestellt</SelectItem>
                  <SelectItem value="sent">Versendet</SelectItem>
                  <SelectItem value="void">Storniert</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Customer Filter */}
            <div className="space-y-1">
              <Label className="text-muted-foreground text-xs">Kunde</Label>
              <SearchableSelect
                options={[
                  { value: '', label: 'Alle Kunden' },
                  ...customers.map(c => ({ value: c.id, label: c.name }))
                ]}
                value={filters.customer_id}
                onValueChange={(value) => setFilters({...filters, customer_id: value})}
                placeholder="Alle Kunden"
              />
            </div>
            
            {/* Date From */}
            <div className="space-y-1">
              <Label className="text-muted-foreground text-xs">Von</Label>
              <Input
                type="date"
                value={filters.date_from}
                onChange={(e) => setFilters({...filters, date_from: e.target.value})}
                className="bg-input border-border text-foreground"
              />
            </div>
            
            {/* Date To */}
            <div className="space-y-1">
              <Label className="text-muted-foreground text-xs">Bis</Label>
              <Input
                type="date"
                value={filters.date_to}
                onChange={(e) => setFilters({...filters, date_to: e.target.value})}
                className="bg-input border-border text-foreground"
              />
            </div>
            
            {/* Search */}
            <div className="space-y-1">
              <Label className="text-muted-foreground text-xs">Suche</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  value={filters.search}
                  onChange={(e) => setFilters({...filters, search: e.target.value})}
                  placeholder="Nummer, Kunde..."
                  className="bg-input border-border text-foreground pl-9"
                />
              </div>
            </div>
          </div>
          
          {/* Reset button */}
          {hasActiveFilter && (
            <div className="mt-3 flex justify-end">
              <Button type="button" variant="ghost" size="sm" onClick={resetFilters} className="text-muted-foreground">
                <RotateCcw className="w-4 h-4 mr-2" />
                Filter zurücksetzen
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Summary row */}
      <div className="flex flex-wrap gap-4 text-sm">
        <div className="bg-card/50 border border-border rounded-lg px-4 py-2">
          <span className="text-muted-foreground">Anzahl:</span>{' '}
          <span className="text-foreground font-medium">{filterTotals.count}</span>
        </div>
        <div className="bg-card/50 border border-border rounded-lg px-4 py-2">
          <span className="text-muted-foreground">Netto:</span>{' '}
          <span className="text-foreground font-medium">{formatCurrency(filterTotals.net)}</span>
        </div>
        <div className="bg-card/50 border border-border rounded-lg px-4 py-2">
          <span className="text-muted-foreground">Brutto:</span>{' '}
          <span className="text-cyan-400 font-medium">{formatCurrency(filterTotals.gross)}</span>
        </div>
      </div>

      {/* Invoice List */}
      <Card className="bg-card/50 border-border">
        <CardContent className="p-0">
          {loading ? (
            <p className="text-muted-foreground text-center py-8">Laden...</p>
          ) : filteredInvoices.length === 0 ? (
            <p className="text-muted-foreground text-center py-8">Keine Rechnungen gefunden</p>
          ) : (
            <div className="divide-y divide-white/5">
              {filteredInvoices.map((invoice) => (
                <div
                  key={invoice.id}
                  className={`p-4 hover:bg-accent cursor-pointer ${invoice.status === 'void' || invoice.status === 'voided' ? 'opacity-60' : ''}`}
                  onClick={() => openInvoiceDetail(invoice.id)}
                  data-testid={`invoice-row-${invoice.id}`}
                >
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className={`text-foreground font-medium ${invoice.status === 'void' || invoice.status === 'voided' ? 'line-through' : ''}`}>
                          {invoice.invoice_number || invoice.draft_reference}
                        </p>
                        {invoice.is_test_invoice && (
                          <span className="px-2 py-0.5 text-xs rounded bg-amber-500/20 text-amber-600 border border-amber-500/30">
                            Test
                          </span>
                        )}
                        {invoice.is_cancellation && (
                          <span className="px-2 py-0.5 text-xs rounded bg-red-500/20 text-red-600 border border-red-500/30">
                            Storno
                          </span>
                        )}
                      </div>
                      <p className="text-muted-foreground text-sm">
                        {invoice.customer_name} · {formatDate(invoice.period_start)} - {formatDate(invoice.period_end)}
                      </p>
                      {invoice.is_cancellation && invoice.cancels_invoice_number && (
                        <p className="text-muted-foreground text-xs">zu {invoice.cancels_invoice_number}</p>
                      )}
                      {/* Provenance info */}
                      {getProvenanceInfo(invoice) && (
                        <p className="text-muted-foreground text-xs mt-1">{getProvenanceInfo(invoice)}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-3">
                      {invoice.gross_total != null ? (
                        <p className="text-lg font-bold text-cyan-400">{formatCurrency(invoice.gross_total)}</p>
                      ) : (
                        <p className="text-lg font-bold text-cyan-400">
                          {invoice.total_hours?.toFixed(1) || 0}h
                          {invoice.daily_rate_count > 0 && ` + ${invoice.daily_rate_count} TP`}
                        </p>
                      )}
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="w-8 h-8"
                        onClick={(e) => {
                          e.stopPropagation();
                          openPdfPreview(invoice.id);
                        }}
                      >
                        <FileText className="w-4 h-4" />
                      </Button>
                      {getStatusBadge(invoice)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
      </>)}

      {/* Generate Dialog - Two Steps */}
      <Dialog open={isGenerateOpen} onOpenChange={(open) => { setIsGenerateOpen(open); if (!open) setGenerateStep(1); }}>
        <DialogContent className={`bg-card border-border text-foreground ${generateStep === 2 ? 'max-w-3xl' : 'max-w-md'}`}>
          <DialogHeader>
            <DialogTitle>
              {generateStep === 1 ? 'Schritt 1: Zeitraum wählen' : 'Schritt 2: Einträge auswählen'}
            </DialogTitle>
          </DialogHeader>
          
          {generateStep === 1 ? (
            /* Step 1: Filter selection */
            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="text-muted-foreground">Kunde *</Label>
                <Select
                  value={generateForm.customer_id}
                  onValueChange={(value) => setGenerateForm({ ...generateForm, customer_id: value, project_id: '' })}
                >
                  <SelectTrigger className="bg-input border-border text-foreground">
                    <SelectValue placeholder="Kunde auswählen..." />
                  </SelectTrigger>
                  <SelectContent className="bg-card border-border">
                    {customers.filter((c) => !c.is_internal).map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-muted-foreground">Auftrag (optional)</Label>
                <Select
                  value={generateForm.project_id}
                  onValueChange={(value) => setGenerateForm({ ...generateForm, project_id: value })}
                  disabled={!generateForm.customer_id}
                >
                  <SelectTrigger className="bg-input border-border text-foreground">
                    <SelectValue placeholder="Alle Aufträge" />
                  </SelectTrigger>
                  <SelectContent className="bg-card border-border">
                    <SelectItem value="all">Alle Aufträge</SelectItem>
                    {filteredProjects.map((p) => (
                      <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-muted-foreground">Zeitraum von</Label>
                  <Input
                    type="date"
                    value={generateForm.period_start}
                    onChange={(e) => setGenerateForm({ ...generateForm, period_start: e.target.value })}
                    className="bg-input border-border text-foreground"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-muted-foreground">Zeitraum bis</Label>
                  <Input
                    type="date"
                    value={generateForm.period_end}
                    onChange={(e) => setGenerateForm({ ...generateForm, period_end: e.target.value })}
                    className="bg-input border-border text-foreground"
                  />
                </div>
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setIsGenerateOpen(false)}>
                  Abbrechen
                </Button>
                <Button type="button" onClick={handleLoadPreview} disabled={loadingPreview || !generateForm.customer_id}>
                  {loadingPreview ? 'Laden...' : 'Einträge laden'}
                  <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              </DialogFooter>
            </div>
          ) : (
            /* Step 2: Entry selection */
            <div className="space-y-4">
              {/* Summary */}
              <div className="bg-muted/50 rounded-lg p-3 flex flex-wrap gap-4 text-sm">
                <div>
                  <span className="text-muted-foreground">Erfasst:</span>{' '}
                  <span className="text-foreground font-medium">{getSelectedTotals().recordedHours}h</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Abgerechnet:</span>{' '}
                  <span className="text-cyan-400 font-medium">{getSelectedTotals().hours}h</span>
                </div>
                {getSelectedTotals().dailyRates > 0 && (
                  <div>
                    <span className="text-muted-foreground">Tagessätze:</span>{' '}
                    <span className="text-foreground font-medium">{getSelectedTotals().dailyRates}</span>
                  </div>
                )}
                {parseFloat(getSelectedTotals().km) > 0 && (
                  <div>
                    <span className="text-muted-foreground">Kilometer:</span>{' '}
                    <span className="text-foreground font-medium">{getSelectedTotals().km} km</span>
                  </div>
                )}
              </div>

              {/* Project entries */}
              {previewEntries.project_entries.length > 0 && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-muted-foreground">Projektzeit-Einträge ({previewEntries.project_entries.length})</Label>
                    <div className="flex gap-2">
                      <Button type="button" variant="ghost" size="sm" onClick={selectAllProjectEntries}>Alle</Button>
                      <Button type="button" variant="ghost" size="sm" onClick={deselectAllProjectEntries}>Keine</Button>
                    </div>
                  </div>
                  <div className="max-h-60 overflow-y-auto border border-border rounded-lg divide-y divide-white/5">
                    {previewEntries.project_entries.map((entry) => {
                      const hasAdjustment = entry.billed_hours !== null && entry.billed_hours !== undefined && entry.billed_hours !== entry.hours;
                      return (
                        <div 
                          key={entry.id} 
                          className={`p-3 flex items-center gap-3 cursor-pointer hover:bg-accent ${
                            selectedProjectEntries.includes(entry.id) ? 'bg-cyan-500/10' : ''
                          }`}
                          onClick={() => toggleProjectEntry(entry.id)}
                        >
                          <Checkbox 
                            checked={selectedProjectEntries.includes(entry.id)}
                            onCheckedChange={() => toggleProjectEntry(entry.id)}
                            className="border-white/30"
                          />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between gap-2">
                              <p className="text-sm text-muted-foreground">{formatDate(entry.date)}</p>
                              <div className="flex items-center gap-2">
                                {hasAdjustment ? (
                                  <TooltipProvider>
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <span className="text-sm text-amber-400 font-mono cursor-help">
                                          <span className="text-muted-foreground line-through mr-1">{entry.hours?.toFixed(2)}</span>
                                          {effHours(entry).toFixed(2)}h
                                        </span>
                                      </TooltipTrigger>
                                      <TooltipContent className="bg-card border-border">
                                        <p className="text-xs">{entry.billing_adjustment_reason || 'Angepasst'}</p>
                                      </TooltipContent>
                                    </Tooltip>
                                  </TooltipProvider>
                                ) : (
                                  <span className="text-sm text-foreground font-mono">
                                    {entry.is_daily_rate ? 'Tagessatz' : `${entry.hours?.toFixed(2)}h`}
                                  </span>
                                )}
                                {canAdjust && !entry.is_daily_rate && (
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    className="w-6 h-6"
                                    onClick={(e) => { e.stopPropagation(); openAdjustDialog(entry); }}
                                  >
                                    <Euro className="w-3 h-3" />
                                  </Button>
                                )}
                                {entry.resolved_rate > 0 ? (
                                  <span className="text-xs text-lime-500">{entry.resolved_rate.toFixed(2)} EUR/h</span>
                                ) : (
                                  <span className="text-xs text-red-400">Kein Satz</span>
                                )}
                              </div>
                            </div>
                            <p className="text-sm text-foreground truncate">{entry.project_name}</p>
                            {entry.description && (
                              <p className="text-xs text-muted-foreground truncate mt-1">{entry.description}</p>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Mileage entries */}
              {previewEntries.mileage_entries.length > 0 && (
                <div className="space-y-2">
                  <Label className="text-muted-foreground">Kilometer-Einträge ({previewEntries.mileage_entries.length})</Label>
                  <div className="max-h-40 overflow-y-auto border border-border rounded-lg divide-y divide-white/5">
                    {previewEntries.mileage_entries.map((entry) => (
                      <div 
                        key={entry.id} 
                        className={`p-3 flex items-center gap-3 cursor-pointer hover:bg-accent ${
                          selectedMileageEntries.includes(entry.id) ? 'bg-cyan-500/10' : ''
                        }`}
                        onClick={() => toggleMileageEntry(entry.id)}
                      >
                        <Checkbox 
                          checked={selectedMileageEntries.includes(entry.id)}
                          onCheckedChange={() => toggleMileageEntry(entry.id)}
                          className="border-white/30"
                        />
                        <div className="flex-1">
                          <div className="flex items-center justify-between">
                            <p className="text-sm text-muted-foreground">{formatDate(entry.date)}</p>
                            <p className="text-sm text-foreground">{entry.kilometers} km</p>
                          </div>
                          {entry.description && (
                            <p className="text-xs text-muted-foreground">{entry.description}</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Manual Items Section */}
              <div className="space-y-2 border-t border-border pt-4">
                <Label className="text-muted-foreground flex items-center gap-2">
                  <PlusCircle className="w-4 h-4" />
                  Manuelle Positionen ({manualItems.length})
                </Label>
                
                {manualItems.length > 0 && (
                  <div className="border border-amber-500/30 rounded-lg divide-y divide-white/5 bg-amber-500/5">
                    {manualItems.map((item) => (
                      <div key={item.id} className="p-3 flex items-center justify-between">
                        <div>
                          <p className="text-sm text-foreground">{item.description}</p>
                          <p className="text-xs text-muted-foreground">
                            {item.quantity} {item.unit}
                            {item.unit_price > 0 && ` × ${item.unit_price.toFixed(2)} EUR`}
                          </p>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeManualItem(item.id)}
                          className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
                
                <div className="grid grid-cols-12 gap-2 items-end">
                  <div className="col-span-5">
                    <Input
                      placeholder="Beschreibung"
                      value={newManualItem.description}
                      onChange={(e) => setNewManualItem({ ...newManualItem, description: e.target.value })}
                      className="bg-input border-border text-foreground text-sm"
                    />
                  </div>
                  <div className="col-span-2">
                    <Input
                      type="number"
                      min="0.25"
                      step="0.25"
                      placeholder="Menge"
                      value={newManualItem.quantity}
                      onChange={(e) => setNewManualItem({ ...newManualItem, quantity: parseFloat(e.target.value) || 0 })}
                      className="bg-input border-border text-foreground text-sm"
                    />
                  </div>
                  <div className="col-span-2">
                    <Select
                      value={newManualItem.unit}
                      onValueChange={(value) => setNewManualItem({ ...newManualItem, unit: value })}
                    >
                      <SelectTrigger className="bg-input border-border text-foreground text-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-card border-border">
                        <SelectItem value="Stunden">Stunden</SelectItem>
                        <SelectItem value="Stück">Stück</SelectItem>
                        <SelectItem value="Pauschale">Pauschale</SelectItem>
                        <SelectItem value="km">km</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-2">
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder="Preis"
                      value={newManualItem.unit_price || ''}
                      onChange={(e) => setNewManualItem({ ...newManualItem, unit_price: parseFloat(e.target.value) || 0 })}
                      className="bg-input border-border text-foreground text-sm"
                    />
                  </div>
                  <div className="col-span-1">
                    <Button
                      type="button"
                      onClick={addManualItem}
                      size="sm"
                      className="w-full bg-amber-600 hover:bg-amber-700"
                    >
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-between gap-3 pt-4 border-t border-border">
                <Button type="button" variant="outline" onClick={() => setGenerateStep(1)}>
                  <ChevronLeft className="w-4 h-4 mr-2" />
                  Zurück
                </Button>
                <div className="flex gap-2">
                  <Button type="button" variant="outline" onClick={() => setIsGenerateOpen(false)}>
                    Abbrechen
                  </Button>
                  <Button 
                    type="button"
                    onClick={openConfirmGenerateDialog} 
                    disabled={selectedProjectEntries.length === 0 && selectedMileageEntries.length === 0 && manualItems.length === 0}
                    className="bg-cyan-600 hover:bg-cyan-700"
                  >
                    Rechnung erstellen
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Generate Invoice Confirmation Dialog */}
      <Dialog open={confirmGenerateOpen} onOpenChange={setConfirmGenerateOpen}>
        <DialogContent className="bg-card border-border text-foreground max-w-md">
          <DialogHeader>
            <DialogTitle className="text-foreground">Rechnungsentwurf erzeugen</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Period */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-muted-foreground">Leistungszeitraum von</Label>
                <Input
                  type="date"
                  value={confirmGenerateData.periodStart}
                  onChange={(e) => setConfirmGenerateData({ ...confirmGenerateData, periodStart: e.target.value })}
                  className="bg-input border-border text-foreground"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-muted-foreground">bis</Label>
                <Input
                  type="date"
                  value={confirmGenerateData.periodEnd}
                  onChange={(e) => setConfirmGenerateData({ ...confirmGenerateData, periodEnd: e.target.value })}
                  className="bg-input border-border text-foreground"
                />
              </div>
            </div>
            
            {/* Reference */}
            {confirmGenerateData.customerInvoiceOptions?.reference_type && (
              <div className="space-y-2">
                <Label className="text-muted-foreground">
                  {getReferenceLabel(confirmGenerateData.customerInvoiceOptions.reference_type)}
                  {confirmGenerateData.customerInvoiceOptions.require_reference && ' *'}
                </Label>
                <Input
                  value={confirmGenerateData.referenceValue}
                  onChange={(e) => setConfirmGenerateData({ ...confirmGenerateData, referenceValue: e.target.value })}
                  className="bg-input border-border text-foreground"
                  placeholder={confirmGenerateData.customerInvoiceOptions.require_reference ? 'Erforderlich' : 'Optional'}
                />
              </div>
            )}
            
            {/* Note */}
            <div className="space-y-2">
              <Label className="text-muted-foreground">Einleitungstext (optional)</Label>
              <Textarea
                value={confirmGenerateData.invoiceNote}
                onChange={(e) => setConfirmGenerateData({ ...confirmGenerateData, invoiceNote: e.target.value })}
                placeholder="Erscheint unter dem Leistungszeitraum..."
                rows={2}
                className="bg-input border-border text-foreground"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setConfirmGenerateOpen(false)}>
              Abbrechen
            </Button>
            <Button
              type="button"
              onClick={handleConfirmGenerate}
              disabled={isGeneratingInvoice}
              className="bg-cyan-600 hover:bg-cyan-700"
            >
              {isGeneratingInvoice ? 'Erstelle...' : 'Entwurf erzeugen'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* PDF Preview Dialog */}
      <Dialog open={pdfDialogOpen} onOpenChange={closePdfPreview}>
        <DialogContent className="bg-card border-border text-foreground max-w-4xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="text-foreground flex items-center justify-between">
              <span>PDF-Vorschau</span>
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => window.open(pdfBlobUrl, '_blank')}
                  disabled={!pdfBlobUrl}
                  className="border-border"
                >
                  <ExternalLink className="w-4 h-4 mr-2" />
                  In neuem Tab öffnen
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={downloadPdf}
                  disabled={!pdfBlobUrl}
                  className="border-border"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Herunterladen
                </Button>
              </div>
            </DialogTitle>
          </DialogHeader>
          
          {pdfLoading ? (
            <div className="flex items-center justify-center h-[75vh]">
              <div className="w-8 h-8 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : pdfBlobUrl ? (
            <PdfCanvasPreview
              blob={pdfBlob}
              fallback={
                <div className="flex flex-col items-center justify-center text-muted-foreground gap-4">
                  <p className="text-sm">Die Vorschau konnte nicht geladen werden.</p>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => window.open(pdfBlobUrl, '_blank')}
                    className="border-border"
                  >
                    <ExternalLink className="w-4 h-4 mr-2" />
                    In neuem Tab öffnen
                  </Button>
                </div>
              }
            />
          ) : (
            <div className="flex items-center justify-center h-[75vh] text-muted-foreground">
              Kein PDF verfügbar
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Billing Quantity Adjustment Dialog */}
      <Dialog open={adjustDialogOpen} onOpenChange={setAdjustDialogOpen}>
        <DialogContent className="bg-card border-border sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-foreground">
              Abrechnungsmenge anpassen
            </DialogTitle>
            {adjustingEntry && (
              <DialogDescription className="text-muted-foreground">
                {adjustingEntry.project_name} · {formatDate(adjustingEntry.date)} · {adjustingEntry.user_name}
              </DialogDescription>
            )}
          </DialogHeader>

          {adjustingEntry && (
            <div className="space-y-4 py-4">
              <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                <span className="text-sm text-muted-foreground">Erfasst</span>
                <span className="text-sm font-mono text-foreground">{adjustingEntry.hours?.toFixed(2)} h</span>
              </div>

              <div className="space-y-2">
                <Label className="text-muted-foreground">Abgerechnet</Label>
                <Input
                  type="number"
                  step="0.25"
                  min="0"
                  value={adjustForm.billed_hours}
                  onChange={(e) => setAdjustForm({ ...adjustForm, billed_hours: e.target.value })}
                  className="bg-input border-border text-foreground font-mono"
                  placeholder="z.B. 2.50"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-muted-foreground">Grund</Label>
                <Select
                  value={adjustForm.reason}
                  onValueChange={(value) => setAdjustForm({ ...adjustForm, reason: value })}
                >
                  <SelectTrigger className="bg-input border-border text-foreground">
                    <SelectValue placeholder="Grund auswählen..." />
                  </SelectTrigger>
                  <SelectContent className="bg-card border-border">
                    {adjustmentReasons.map((reason) => (
                      <SelectItem key={reason} value={reason}>{reason}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {adjustForm.reason === 'Sonstiges' && (
                <div className="space-y-2">
                  <Label className="text-muted-foreground">Grund angeben</Label>
                  <Input
                    value={adjustForm.reason_other}
                    onChange={(e) => setAdjustForm({ ...adjustForm, reason_other: e.target.value })}
                    className="bg-input border-border text-foreground"
                    placeholder="Bitte Grund eingeben..."
                  />
                </div>
              )}
            </div>
          )}

          <DialogFooter className="flex gap-2">
            {adjustingEntry?.billed_hours !== null && adjustingEntry?.billed_hours !== undefined && (
              <Button
                type="button"
                variant="outline"
                onClick={handleRemoveAdjustment}
                disabled={isAdjusting}
                className="mr-auto text-amber-500"
              >
                Anpassung entfernen
              </Button>
            )}
            <Button type="button" variant="outline" onClick={() => setAdjustDialogOpen(false)}>
              Abbrechen
            </Button>
            <Button
              type="button"
              onClick={handleAdjustSubmit}
              disabled={isAdjusting}
              className="bg-indigo-500 hover:bg-indigo-600"
            >
              {isAdjusting ? 'Speichern...' : 'Speichern'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* GoBD Delete Confirmation Dialog */}
      <GoBDDeleteConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title={deleteConfig.title}
        itemDescription={deleteConfig.itemDescription}
        requireReason={deleteConfig.requireReason}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
      />

      {/* Audit History Panel Dialog */}
      <Dialog open={historyPanelOpen} onOpenChange={setHistoryPanelOpen}>
        <DialogContent className="bg-card border-border max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-foreground flex items-center gap-2">
              <History className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
              Änderungshistorie
            </DialogTitle>
          </DialogHeader>
          {historyEntry.id && (
            <AuditHistoryPanel
              collectionName={historyEntry.collection}
              documentId={historyEntry.id}
              isOpen={true}
              compact={false}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
